import { pxToEm } from 'util/pxToEm';
import { formatDate } from 'util/formatDate';
import { formatTime } from 'util/formatTime';
import { Box, Typography, useTheme } from '@mui/material';
import {
  CopyIcon,
  FacebookCircleIcon,
  XLogoIcon,
  MoreSharingIcon,
} from 'components/icons';
import React, { ReactElement, useEffect, useState } from 'react';
import copy from 'copy-to-clipboard';
import { Event } from 'services/api';
import { Button } from 'components/ui';
import { Image as LibImage, TruncateText } from 'components/lib';
import { useSnackbar } from 'notistack';

export interface EventSharingWidgetProps {
  event: Event;
}

interface ReactNativeWebView {
  postMessage: (message: string) => void;
}

declare global {
  interface Window {
    ReactNativeWebView?: ReactNativeWebView;
  }
}

export const EventSharingWidget = ({
  event,
}: EventSharingWidgetProps): ReactElement => {
  const theme = useTheme();
  const { name, date, startTime, endTime, flyerImageUrl } = event;

  const eventLink = new URL(event.url, window.location.href).href;

  const { enqueueSnackbar } = useSnackbar();

  const copyLink = () => {
    copy(eventLink);
    setRecentlyCopied(true);
  };

  const [wasRecentlyCopied, setRecentlyCopied] = useState(false);

  useEffect(() => {
    if (!wasRecentlyCopied) return;

    const timer = setTimeout(() => {
      setRecentlyCopied(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [wasRecentlyCopied]);

  const openFacebookDialog = () => {
    const left = Math.round((window.outerWidth - 600) / 2) + window.screenX;
    const top = window.screenY + 160;

    const shareDialog = window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        eventLink,
      )}`,
      'facebook-share',
      `width=600, height=400, top=${top}, left=${left}, scrollbars=no`,
    );
    shareDialog?.moveTo(left, top);
  };

  const twitterHashtags = [
    event.school.name.replace(/\s+/g, '-').replace(/\W/g, ''),
    event.name.replace(/\s+/g, '-').replace(/\W/g, ''),
    'CampusLush',
  ].join(',');

  const openTwitterDialog = () => {
    const width = Math.min(600, window.outerWidth);
    const left = Math.round((window.outerWidth - 600) / 2) + window.screenX;
    const top = window.screenY + 160;

    const shareDialog = window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        eventLink,
      )}&hashtags=${twitterHashtags}`,
      'twitter-share',
      `width=${width}, height=360, top=${top}, left=${left}, scrollbars=no`,
    );
    shareDialog?.moveTo(left, top);
  };

  const checkIfCanShare = () => {
    return (navigator && navigator.share) || isRunningInWebView;
  };

  const isRunningInWebView = (): boolean => {
    if (window?.ReactNativeWebView !== undefined) return true;

    return false;
  };

  const isRunningInAndroid = (): boolean => {
    const userAgent: string = navigator.userAgent || navigator.vendor;

    // Check if the environment is Android
    const isAndroid = /android/i.test(userAgent);

    // Return true if both conditions are met
    return isAndroid;
  };

  const onMoreShareClick = async () => {
    const file: File[] = [];

    try {
      const response = await fetch(flyerImageUrl, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'omit',
      });
      if (response.ok) {
        const blob = await response.blob();
        file.push(new File([blob], `${name}.jpeg`, { type: 'image/jpeg' }));
      }

      // Check if running in Android WebView by looking for window.Android
      if (isRunningInWebView() && isRunningInAndroid()) {
        const reader = new FileReader();
        reader.onloadend = function () {
          const base64data = reader.result;
          window?.ReactNativeWebView?.postMessage(
            JSON.stringify({
              action: 'shareContent',
              title: name,
              url: eventLink,
              imageBase64: base64data,
            }),
          );
        };
        reader.readAsDataURL(file[0]);
      } else if (navigator.canShare({ files: file })) {
        await navigator.share({
          title: name,
          url: eventLink,
          files: file,
        });
      } else {
        await navigator.share({
          title: name,
          url: eventLink,
        });
      }
    } catch (error: unknown) {
      let errorMessage = 'Something went wrong';
      if (error instanceof Error) errorMessage = error.message;

      if (
        !errorMessage.includes('Share canceled') &&
        !errorMessage.includes('AbortError') &&
        !errorMessage.includes('Abort due to cancellation of share')
      )
        enqueueSnackbar('Something went wrong', { variant: 'error' });
    }
  };

  const eventDetails = (
    <Box display="flex" mb={24} flexDirection={{ xs: 'column', md: 'row' }}>
      <LibImage
        src={flyerImageUrl}
        alt={name}
        height="auto"
        style={{
          marginRight: 16,
          maxWidth: 200,
        }}
      />
      <Box
        display="flex"
        justifyContent="center"
        flexDirection={'column'}
        mt={{ xs: 8, md: 'unset' }}
        maxWidth={{ xs: 'unset', md: 250 }}
      >
        <Typography
          mb={8}
          style={{
            fontSize: pxToEm(16),
            fontWeight: 500,
          }}
        >
          {' '}
          <TruncateText lines={2}>{name}</TruncateText>
        </Typography>
        <Typography
          style={{
            fontSize: pxToEm(12),
            fontWeight: 500,
            color: theme.palette.neutral.darkest,
            whiteSpace: 'nowrap',
          }}
        >
          {formatDate(new Date(date), {
            includeDay: true,
            includeDate: true,
            includeYear: true,
          })}
        </Typography>
        <Typography
          style={{
            fontSize: pxToEm(12),
            fontWeight: 500,
            color: theme.palette.neutral.darkest,
            whiteSpace: 'nowrap',
          }}
        >
          {startTime && formatTime(startTime)}
          {startTime && endTime && ' - '}
          {endTime && formatTime(endTime)}
        </Typography>
      </Box>
    </Box>
  );

  const shareButtons = (
    <Box display="flex" flexWrap="wrap">
      <Button
        variant="paper"
        sx={{
          marginRight: 16,
          marginBottom: 24,
          height: 60,
          width: { xs: '100%', sm: 'unset' },
          justifyContent: 'start',
        }}
        onClick={copyLink}
      >
        <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
          <CopyIcon />
        </Box>
        <Typography>{wasRecentlyCopied ? 'Copied!' : 'Copy Link'}</Typography>
      </Button>
      <Button
        variant="paper"
        sx={{
          marginRight: 16,
          marginBottom: 24,
          height: 60,
          width: { xs: '100%', sm: 'unset' },
          justifyContent: 'start',
        }}
        onClick={openFacebookDialog}
      >
        <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
          <FacebookCircleIcon />
        </Box>
        <Typography>Facebook</Typography>
      </Button>
      <Button
        variant="paper"
        sx={{
          marginRight: 16,
          marginBottom: 24,
          height: 60,
          width: { xs: '100%', sm: 'unset' },
          justifyContent: 'start',
        }}
        onClick={openTwitterDialog}
      >
        <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
          <XLogoIcon />
        </Box>
        <Typography>X</Typography>
      </Button>
      {checkIfCanShare() && (
        <Button
          variant="paper"
          sx={{
            marginRight: 16,
            marginBottom: 24,
            height: 60,
            width: { xs: '100%', sm: 'unset' },
            justifyContent: 'start',
          }}
          onClick={onMoreShareClick}
        >
          <Box
            mr={8}
            display="flex"
            alignItems={'center'}
            fontSize={pxToEm(30)}
          >
            <MoreSharingIcon />
          </Box>
          <Typography>More Sharing</Typography>
        </Button>
      )}
    </Box>
  );

  return (
    <Box display="flex" flexDirection={'column'}>
      {eventDetails}
      {shareButtons}
    </Box>
  );
};
